interface HornDescription {
  Horned: string;
  Tipped: string;
  'Incomplete dehorned': string;
  Scurs: string;
  Dehorned: string;
  Disbudded: string;
  'No Horns': string;
}

export const booleanHandler = (item: string | boolean | null) => {
  return item === 'true' ? true : false;
};

const hornedDescription = {
  Horned: 'No portion of the horn has been removed',
  Tipped: 'The tip of the horn has been removed',
  'Incomplete dehorned': 'There is some horn growth following dehorning',
  Scurs:
    'Incompletely developed horn-like structures generally only attached to the skin',
  Dehorned: 'The horn has been removed leaving no protrusion from the skull',
  Disbudded:
    'The immature horn bud and the surrounding area of skin is removed (from a youngcalf) prior to solid attachment to the skull',
  'No Horns': 'Poll breeds',
};

export const hornedDescriptionLookup = (
  hornType: keyof HornDescription
): string => {
  return hornedDescription[hornType];
};

interface IGenerateListingName {
  quantity: string | number;
  pregStatus: string[];
  breed: string;
  purebredBreed?: string;
  mixedBreed?: string;
  gender: string[];
  productType: string;
  listingType?: string;
}

export const generateListingName = ({
  quantity,
  pregStatus,
  breed,
  purebredBreed,
  mixedBreed,
  gender,
  productType,
  listingType,
}: IGenerateListingName) => {
  let name = '';

  if (quantity && listingType !== 'BUY') {
    name += `${quantity} `;
  }

  if (pregStatus && pregStatus.length > 0) {
    name += `${pregStatus.join('/')} `;
  }

  if (breed === 'Purebred' && purebredBreed) {
    name += `${purebredBreed} `;
  } else if (breed === 'Crossbred' && mixedBreed) {
    name += `${mixedBreed} `;
  } else if (breed && breed !== 'Crossbred' && breed !== 'Purebred') {
    name += `${breed} `;
  }

  if (gender && gender.length > 0) {
    name += `${gender.join('/')} `;
  }

  return name.trim();
};

/**
 * Turn a listing title into a URL-safe, decorative slug segment. Purely
 * cosmetic — a listing always loads by its id regardless of (or without) the
 * slug — so this only needs to be stable and readable, not reversible.
 *   "30 Angus Steers, Dubbo NSW" -> "30-angus-steers-dubbo-nsw"
 */
export const slugifyListingTitle = (title?: string | null): string => {
  if (!title) return '';
  return title
    .normalize('NFKD') // decompose accents so the marks can be stripped
    .replace(/[̀-ͯ]/g, '') // strip combining diacritical marks
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-') // any run of non-alphanumerics -> single dash
    .replace(/^-+|-+$/g, '') // trim leading/trailing dashes
    .slice(0, 80) // keep URLs to a sensible length
    .replace(/-+$/g, ''); // re-trim in case the slice cut mid-dash
};

/**
 * Public marketplace path for a listing, including the decorative slug:
 *   `/marketplace/listing/<id>/<slug>`
 * Prefers the backend-generated slug (`metadata.seo.slug`) and falls back to a
 * title-derived slug for callers/listings without it. Falls back to
 * `/marketplace/listing/<id>` when there's no slug, and to `/marketplace/listing`
 * when there's no id — so callers that only have an id still get a valid path
 * (the listing loads by id either way). Returns a relative path; prefix with an
 * origin for absolute URLs (canonical, sitemap, share).
 */
export const buildPublicListingPath = (
  listing?: {
    id?: string | null;
    title?: string | null;
    metadata?: { seo?: { slug?: string | null } | null } | null;
  } | null
): string => {
  const base = '/marketplace/listing';
  if (!listing?.id) return base;
  const slug =
    listing.metadata?.seo?.slug || slugifyListingTitle(listing.title);
  return slug ? `${base}/${listing.id}/${slug}` : `${base}/${listing.id}`;
};
